import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, Save, RefreshCw, X, Award, Eye, EyeOff
} from 'lucide-react';
import ImageUploader from '../common/ImageUploader';

const COLOR_OPTIONS = [
  { label: 'Gray', value: 'from-gray-600 to-gray-800' },
  { label: 'Blue', value: 'from-blue-600 to-blue-800' },
  { label: 'Light Blue', value: 'from-blue-400 to-blue-600' },
  { label: 'Orange/Red', value: 'from-blue-500 to-red-600' },
  { label: 'Teal/Green', value: 'from-teal-500 to-green-600' },
  { label: 'Red', value: 'from-red-500 to-red-700' },
  { label: 'Red/Orange', value: 'from-red-600 to-blue-600' },
  { label: 'Purple', value: 'from-blue-500 to-blue-700' },
  { label: 'Indigo/Purple', value: 'from-indigo-500 to-blue-600' },
  { label: 'Emerald/Teal', value: 'from-emerald-500 to-teal-600' },
  { label: 'Pink/Purple', value: 'from-blue-500 to-blue-500' },
];

const BrandsManager = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState({
    name: '',
    logo: '',
    description: '',
    color: 'from-blue-500 to-blue-500',
    order: 0,
    is_active: true,
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchBrands(); }, []);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/brands/admin`, { credentials: 'include' });
      if (r.ok) setBrands(await r.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '', logo: '', description: '',
      color: 'from-blue-500 to-blue-500',
      order: brands.length, is_active: true,
    });
    setErrorMsg('');
  };

  const startEdit = (b) => {
    setEditingId(b.brand_id);
    setShowAddForm(false);
    setForm({
      name: b.name || '',
      logo: b.logo || '',
      description: b.description || '',
      color: b.color || 'from-blue-500 to-blue-500',
      order: b.order || 0,
      is_active: b.is_active !== false,
    });
    setErrorMsg('');
  };

  const cancel = () => {
    setEditingId(null);
    setShowAddForm(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.logo.trim()) {
      setErrorMsg('Name and logo are required.');
      return;
    }
    setSaving(true);
    setErrorMsg('');
    try {
      const url = editingId ? `${API_URL}/api/brands/${editingId}` : `${API_URL}/api/brands`;
      const method = editingId ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (r.ok) {
        await fetchBrands();
        cancel();
      } else {
        const err = await r.json().catch(() => ({}));
        setErrorMsg(err.detail || 'Failed to save');
      }
    } catch (e) {
      setErrorMsg('Network error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (b) => {
    await fetch(`${API_URL}/api/brands/${b.brand_id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ is_active: !b.is_active }),
    });
    await fetchBrands();
  };

  const deleteBrand = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"? This cannot be undone.`)) return;
    await fetch(`${API_URL}/api/brands/${b.brand_id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    await fetchBrands();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="brands-manager">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Award className="text-blue-600" />
            Brands ({brands.length})
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage the "Brands Worked With" section shown on the homepage.
          </p>
        </div>
        <button
          data-testid="add-brand-btn"
          onClick={() => { setShowAddForm(true); setEditingId(null); resetForm(); }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:shadow-lg transition-all"
        >
          <Plus size={18} /> Add Brand
        </button>
      </div>

      {(showAddForm || editingId) && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              {editingId ? 'Edit Brand' : 'Add New Brand'}
            </h3>
            <button type="button" onClick={cancel} className="text-gray-400 hover:text-gray-700" aria-label="Close">
              <X size={20} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <ImageUploader
              label="Logo *"
              value={form.logo}
              onChange={(url) => setForm((p) => ({ ...p, logo: url }))}
              placeholder="Upload JPG, JPEG or PNG"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Reliance Big Synergy"
                  data-testid="brand-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm((p) => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Accent Color</label>
                <select
                  value={form.color}
                  onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {COLOR_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <div className={`mt-2 h-3 rounded bg-gradient-to-r ${form.color}`} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Short description shown on hover..."
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm text-gray-700">Active (show on homepage)</span>
            </label>
            {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={cancel} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="save-brand-btn"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                {editingId ? 'Update' : 'Add'} Brand
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {brands.map((b) => (
          <div
            key={b.brand_id}
            className={`bg-white rounded-xl border overflow-hidden transition-all ${
              b.is_active ? 'border-gray-200 hover:border-blue-300 hover:shadow-md' : 'border-gray-200 opacity-60'
            }`}
          >
            <div className="relative h-28 bg-gray-50 flex items-center justify-center p-4">
              <img
                src={b.logo}
                alt={b.name}
                className="max-h-full max-w-[80%] object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {!b.is_active && (
                <span className="absolute top-2 left-2 px-2 py-0.5 bg-gray-700 text-white text-xs rounded">Hidden</span>
              )}
            </div>
            <div className="p-3">
              <h4 className="font-semibold text-gray-900 text-sm truncate">{b.name}</h4>
              <p className="text-xs text-gray-500 mt-0.5">Order: {b.order}</p>
              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{b.description}</p>
              <div className="flex items-center justify-end gap-1 mt-3">
                <button
                  onClick={() => toggleActive(b)}
                  title={b.is_active ? 'Hide from homepage' : 'Show on homepage'}
                  className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  {b.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
                </button>
                <button
                  onClick={() => startEdit(b)}
                  title="Edit"
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <Edit2 size={15} />
                </button>
                <button
                  onClick={() => deleteBrand(b)}
                  title="Delete"
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                  data-testid={`delete-brand-${b.brand_id}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {brands.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Award size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-600">No brands yet.</p>
          <button
            onClick={() => { setShowAddForm(true); resetForm(); }}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            Add your first brand
          </button>
        </div>
      )}
    </div>
  );
};

export default BrandsManager;
